import CMSWrapper from '@/features/shared/CMSWrapper';
import SEOHrefLang from '@/features/shared/SEOHrefLang';
import { filterObj } from '@/utils/helpers';
import { CTADocumentMapping, MetadataDocumentMapping } from '@/utils/mappings';
import {
  resolveBool,
  resolveColorName,
  resolveCompound,
  resolveContentBlocksContainer,
  resolveId,
  resolveImage,
  resolveLink,
  resolveRTC,
  resolveTheme,
} from '@/utils/resolvers';
import React, { memo, useMemo, useRef } from 'react';
import { TaxonomyKey, TaxonomyType, TechPartnerTaxonomy } from '../TechPartner.interface';
import TechPartnerDetail from './TechPartnerDetail';

const resolveSummaryBlock = ({ rawVal }: any): TaxonomyType | undefined => {
  if (!rawVal?.length) return undefined;

  let mappedCategories: TechPartnerTaxonomy = {
    industry: { label: 'Industry', values: [] },
    region: { label: 'Availability', values: [] },
    'product-line': { label: 'Product line', values: [] },
  };

  rawVal?.forEach((taxonomy: string) => {
    const splitIndex = taxonomy?.indexOf('/');
    const category = taxonomy?.slice(0, splitIndex);
    const value = taxonomy
      ?.slice(splitIndex + 1)
      ?.split('-')
      ?.filter((word) => !!word?.trim())
      ?.map((word) => word?.[0]?.toUpperCase() + word?.slice(1))
      ?.join(' ');

    if (!value) return;
    if (splitIndex && category && category in mappedCategories) {
      const { label, values } = mappedCategories[category as TaxonomyKey];
      mappedCategories = {
        ...mappedCategories,
        [category]: { label, values: [...values, value] },
      };
    }
  });

  const mappedCategoriesFilter: any = filterObj(
    mappedCategories,
    (_: any, category: { values: string | any[] }) => category.values.length > 0,
  );
  const mappedTaxonomy: TaxonomyType = { categories: mappedCategoriesFilter };

  return mappedTaxonomy;
};

const MappedTechPartnerDetail = ({ config, content, contentButtons, labels, pageModels }: any) => {
  const translationConfig = pageModels?.config?.translation;
  const props = useMemo(
    () => ({
      ...content,
      ...config,
      labels,
      contentButtons,
    }),
    [config, content, contentButtons, labels],
  );
  const hrefLangProps = React.useMemo(
    () => ({
      hrefLangsRaw: content?.pageMetadata?.hreflangUrl,
      exclusionPaths: translationConfig?.endpoint_exclusion_paths,
      countryCodesWhitelist: translationConfig?.country_codes_whitelist,
    }),
    [
      content?.pageMetadata?.hreflangUrl,
      translationConfig?.endpoint_exclusion_paths,
      translationConfig?.country_codes_whitelist,
    ],
  );

  return (
    <div data-datalayer-position="tech_partner_detail">
      <TechPartnerDetail {...props} />
      <SEOHrefLang {...hrefLangProps} />
    </div>
  );
};

const TechPartnerDetailCMSWrapper = (brProps: any) => {
  const mapping = useRef({
    document: [
      'content',
      {
        cardTileImage: ['cardImage', resolveImage],
        includeBreadcrumb: [null, resolveBool],
        includeJumplinks: [null, resolveBool],
        backgroundBannerImage: [null, resolveImage],
        backgroundBannerVideo: [null, resolveCompound],
        mobileBackgroundBanner: [null, resolveImage],
        logo: [null, resolveImage],
        headline: null,
        shortDescription: [null, resolveRTC],
        partnerWebsiteURL: ['partnerWebsiteUrl', resolveRTC],
        pageMetadata: [null, MetadataDocumentMapping],
        cardImage: [null, resolveImage],
        cardDescription: null,
        isGenetecCertified: [null, resolveBool],
        cardButton: [null, resolveLink],
        cardUrl: null,
        socialChannels: null,
        summary: [
          null,
          { keys: ['taxonomy', resolveSummaryBlock], compatibility: [null, resolveLink] },
        ],
        taxonomy: null,
        memberProfile: [
          null,
          {
            includeJumplinks: [null, resolveBool],
            cta: [
              null,
              {
                ctaContent: [
                  'ctaContent',
                  {
                    document: ['content', CTADocumentMapping],
                  },
                ],
                textColor: [null, resolveTheme('bg-color')],
                themeBackgroundColor: [null, resolveColorName],
                topContainerBackgroundColor: [null, resolveColorName],
                topPadding: '',
              },
            ],
            detailContent: ['detailContent', resolveContentBlocksContainer],
          },
        ],
        id: [null, resolveId],
      },
    ],
    'technologyPartnersPage.config': 'config',
    'technologyPartnersPage.labels': 'labels',
  });

  const contentButtonNames = useRef({
    document: 'content',
  });

  return (
    <CMSWrapper
      brProps={brProps}
      render={MappedTechPartnerDetail}
      mapping={mapping.current}
      contentButtonNames={contentButtonNames.current}
    />
  );
};

export default memo(TechPartnerDetailCMSWrapper);
